<script lang="ts" setup>
import FloorPlan from './FloorPlan.vue';
import GravityForm from './GravityForm.vue';


const props = defineProps<{
    homeDesignCategories: any,
    homeDesignDetails: any,
    enquiryForm: number,
    title: string
}>();

const homeDesignVariations = props?.homeDesignDetails?.homeDesignVariations;

  const selectedDesignId = ref(null)
  
  const selectedDesign = computed(() => {
    return homeDesignVariations.find((design: any) => design.variationName === selectedDesignId.value) || homeDesignVariations[0];
  });
  
  const selectDesign = (design: any) => {
    selectedDesignId.value = design.variationName
  }

  const strippedDescription = computed(() => {
    return selectedDesign.value?.description
      ?.replace(/<p[^>]*>/g, '')
      ?.replace(/<\/p>/g, '<br>') || '';
  });

  onMounted(() => {
    if (homeDesignVariations.length > 0) {
      selectedDesignId.value = homeDesignVariations[0].variationName
    }
  })

</script>
<template>
    <Slider :imageSlides="selectedDesign?.gallery" :slideId="selectedDesign.variationName" />
    <div class="section-spacer"></div>
    <div class="home-design-section hd-item-wrapper">
        <div class="container-xl">
        <!-- Header -->
        <div class="home-header w-100">
            <div class="d-flex w-100 justify-content-between">
                <div class="hd-header-wrapper">
                    <div class="home-specs-primary">
                      <div class="header-numbers d-flex d-md-none">
                          <button class="variation-link border-0" v-for="(design, index) in homeDesignVariations" @click="selectDesign(design)">
                            <h5
                            :key="design.variationName"
                            :class="['number', 'ms-4', { active: selectedDesign.variationName === design.variationName }]"
                            >
                            {{ design.variationName }}
                            </h5>
                          </button>
                      </div>
                      <div><h2 class="text-white">{{ title }}</h2></div>
                    </div>
                    <div class="home-specs text-secondary">
                        <span>{{ selectedDesign.bedrooms[0] || 0 }} Bed</span>
                        <span class="mx-3 details-separator">&bullet;</span>
                        <span>{{ selectedDesign.bathrooms[0] || 0 }} Bath</span>
                        <span class="mx-3 details-separator">&bullet;</span>
                        <span>{{ selectedDesign.carSpaces[0] || 0 }} Car</span>
                        <span class="mx-3 details-separator">&bullet;</span>
                        <span>{{ selectedDesign.totalFloorAreaSqm?.toLocaleString() }}m2</span>
                    </div>
                </div>
                <div class="header-numbers d-md-flex d-none">
                    <button class="variation-link border-0" v-for="(design, index) in homeDesignVariations" @click="selectDesign(design)">
                      <h5
                      :key="design.variationName"
                      :class="['number', 'ms-4', { active: selectedDesign.variationName === design.variationName }]"
                      >
                      {{ design.variationName }}
                      </h5>
                    </button>
                </div>
            </div>
        </div>
    
        <!-- Description -->
        <div class="description-div" v-html="strippedDescription"></div>
    
        <!-- Main Content with Tabs -->
        <div class="main-content">
            <!-- Tab Buttons -->
            <div class="tab-buttons">
              <button 
                  v-for="design in homeDesignVariations" 
                  :key="design.variationName"
                  :class="['tab-btn', 'text-dark-2', { active: selectedDesign.variationName === design.variationName }]"
                  @click="selectDesign(design)"
              >
                  {{ design.variationName }}
              </button>
            </div>
    
            <!-- Tab Content Area -->
            <div class="tab-content-area">
            <div class="d-flex flex-md-row flex-column h-100">
                <!-- Floor Plan -->
                <div class="col-md-5">
                    <div class="floor-plan-container d-flex justify-content-center">
                        <div v-if="selectedDesign?.floorPlan" class="floor-plan-image">
                          <button class="floor-plan-zoom border-0" aria-label="View Floor Plan" data-bs-toggle="modal" data-bs-target="#floor-plan-modal">
                            <svg xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 43.75 43.75">
                              <g id="Group_319" data-name="Group 319" transform="translate(-1518.576 -2228.861)">
                                <circle id="Ellipse_9" data-name="Ellipse 9" cx="21.336" cy="21.336" r="21.336" transform="translate(1519.115 2229.4)" fill="none" stroke="#353535" stroke-width="1.078"/>
                                <g id="Group_318" data-name="Group 318">
                                  <path id="Path_999" data-name="Path 999" d="M1539.655,2258.654v-7.181h-6.521v-1.591h6.521v-7.065h1.591v7.065h6.522v1.591h-6.522v7.181Z" fill="#353535"/>
                                </g>
                              </g>
                            </svg>
                          </button>
                          <NuxtImg loading="lazy"
                          :src="selectedDesign?.floorPlan?.node?.mediaItemUrl"
                          height="450"
                          width="350" :alt="selectedDesign?.floorPlan?.node?.title || 'Floor Plan Image'" class="plan-img"/>
                        </div>
                        <div class="d-flex justify-content-center align-items-center" v-else>
                          <span>Floor plan not available</span>
                        </div>
                    </div>
                </div>
                <FloorPlan
                    :floorPlanImg="selectedDesign?.floorPlan?.node"
                    :floorPlanFlippedImg="selectedDesign?.floorPlanFlipped?.node"
                />
                <!-- Home Info -->
                <div class="col-md-7 design-info-content px-md-4 px-0">
                    <div class="home-info ps-md-4 ps-0">
                        <div class="area-info mb-5">
                          <h5 class="mb-2 fw-bold">{{ selectedDesign.totalFloorAreaSqm }}m²</h5>
                          <div class="dimensions text-primary">
                              <h5 class="fw-bold">Width:</h5><h5>{{ selectedDesign.houseWidth }}m</h5>
                              <h5 class="fw-bold">Length:</h5><h5>{{ selectedDesign.houseLength }}m</h5>
                          </div>
                        </div>
                        
                        <div class="features-section">
                          <h4 class="text-lg fw-bold">Home Features</h4>
                          <hr class="feature-divider mb-4"/>
                          <div 
                              v-for="(feature, index) in selectedDesign.keyFeatures" 
                              :key="index"
                              class="feature-item text-primary ms-3"
                          >
                              <h5 class="mb-0">{{ feature?.feature }}</h5>
                          </div>
                          <div 
                              v-for="(optionItem, index) in selectedDesign.designOptions" 
                              :key="index"
                              class="design-options text-primary ms-3"
                          >
                              <h5 class="mb-0">{{ optionItem?.option }}</h5>
                          </div>
                        </div>
                    </div>
                    <div class="action-buttons d-flex ps-md-4 ps-0">
                      <button aria-label="enquire" class="btn btn-primary reversed-hover rounded-pill me-md-4 me-0" data-bs-toggle="modal" data-bs-target="#enquiry-form-modal">ENQUIRE</button>
                      <a v-if="selectedDesign?.brochure?.node?.mediaItemUrl" class="btn btn-secondary rounded-pill with-border" download :href="selectedDesign?.brochure?.node?.mediaItemUrl || '/'" target="_blank">
                        DOWNLOAD PLAN
                      </a>
                      <button v-else class="btn btn-secondary rounded-pill with-border" disabled>
                        DOWNLOAD PLAN
                      </button>
                    </div>
                </div>
            </div>
            </div>
        </div>
      </div>
      <!-- MODAL -->
        <div class="modal fade" tabindex="-1" aria-labelledby="enquiry form modal" aria-hidden="true" id="enquiry-form-modal">
          <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                  <div class="modal-header">
                      <h1 class="modal-title fs-5" id="enquire-modal-label">Enquire</h1>
                      <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div class="modal-body p-4">
                      <div class="form-container">
                        <GravityForm formId="enquiry-form" :gravityFormId="enquiryForm"/>
                      </div>
                  </div>
              </div>
          </div>
        </div>
      <!-- END OF MODAL -->
    </div>
</template>